import { useState } from 'react';

import { cloneDeep, set } from 'lodash-es';

import {
  useSuspenseGetContext,
  useSuspenseGetStateByStateKey,
  useSuspensePutState,
} from '../../../api/lending/lapiHooks.suspense';

export const useSuspenseUpdateTurnover = () => {
  const [now] = useState(() => Date.now());
  const { state } = useSuspenseGetStateByStateKey();
  const { context } = useSuspenseGetContext();
  const { putState, isPending } = useSuspensePutState();

  const turnoverLastUpdated = Date.parse(
    state?.application?.company?.last_12_months_turnover?.datetime || '',
  );
  const dateJoined = Date.parse(context?.user_date_joined || '');
  const previousTurnover =
    state?.application?.company?.last_12_months_turnover?.amount;

  const updateTurnover = async (turnover: number) => {
    if (!state) return;
    const newState = cloneDeep(state);
    set(newState, 'application.company.last_12_months_turnover', {
      amount: Math.floor(turnover),
      datetime: new Date().toISOString(),
    });
    await putState({ data: newState });
  };

  const shouldUpdateTurnover = () => {
    const sixMonthsInMilliseconds = 6 * 30 * 24 * 60 * 60 * 1000;
    const hasDeclaredTurnover = Boolean(
      state?.application?.company?.last_12_months_turnover?.amount,
    );

    // --- Not logged in ---

    if (!context.isLoggedIn) {
      return false;
    }

    // --- No turnover declared ---

    if (!hasDeclaredTurnover) {
      // If turnover is not declared, ask for update if user is a seller.
      // If user is not a seller, they should go back to the signup flow.
      return context.isSeller;
    }

    // --- Turnover declared ---

    if (
      turnoverLastUpdated &&
      turnoverLastUpdated > now - sixMonthsInMilliseconds
    ) {
      // Turnover declared and updated within the last 6 months - no need to update
      return false;
    }

    if (!turnoverLastUpdated && dateJoined > now - sixMonthsInMilliseconds) {
      // Fallback incase turnoverLastUpdated is not available - use dateJoined instead
      return false;
    }

    // Turnover declared but not updated in the last 6 months - require update
    return true;
  };

  return {
    shouldUpdateTurnover: shouldUpdateTurnover(),
    previousTurnover,
    updateTurnover,
    isPending,
  };
};
